import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, HardHat, MapPin, Wrench, ShieldCheck, Inbox, FileCheck2, Check, Phone, CalendarClock, ClipboardList, FileText, StickyNote } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/site/Logo";
import { AttachmentList } from "../components/site/AttachmentList";
import { NotificationBell } from "../components/site/NotificationBell";
import { MessagesInbox } from "../components/site/MessagesInbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPROVAL_BADGE = {
  approved: "bg-[#C7F0D8] text-[#0f6b3d]",
  pending_approval: "bg-[#FFE8B0] text-[#7a5b00]",
  suspended: "bg-[#FBD5D5] text-[#9b1c1c]",
};
const APPROVAL_LABEL = { approved: "Approved", pending_approval: "Pending Approval", suspended: "Suspended" };

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Chip = ({ children }) => (
  <span className="inline-block rounded-full bg-[#EAF7FC] text-[#133955] text-xs font-semibold px-3 py-1">{children}</span>
);

export default function ContractorPortal() {
  const { token, user, role, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [leads, setLeads] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [openRepairId, setOpenRepairId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!authLoading && !token) navigate("/portal/login");
    if (!authLoading && token && role && role !== "contractor") navigate("/admin");
  }, [authLoading, token, role, navigate]);

  const loadLeads = useCallback(async () => {
    const { data } = await axios.get(`${API}/contractor/leads`, { headers: { Authorization: `Bearer ${token}` } });
    setLeads(data.leads || []);
  }, [token]);

  const loadRepairs = useCallback(async () => {
    const { data } = await axios.get(`${API}/contractor/repair-requests`, { headers: { Authorization: `Bearer ${token}` } });
    setRepairs(data.requests || []);
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let active = true;
    (async () => {
      try {
        const me = await axios.get(`${API}/contractor/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (active) setProfile(me.data);
        await loadLeads();
        await loadRepairs();
      } catch (err) {
        if (err?.response?.status === 401) { logout(); navigate("/portal/login"); return; }
        if (active) setError("Failed to load your portal.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token, logout, navigate, loadLeads, loadRepairs]);

  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link: /portal?lead=<id> or /portal?repair=<id> opens the matching detail from a notification
  useEffect(() => {
    if (loading) return;
    const lead = searchParams.get("lead");
    const repair = searchParams.get("repair");
    if (!lead && !repair) return;
    if (lead) {
      if (leads.some((l) => l.id === lead)) setOpenId(lead);
      else { toast.error("That project is not assigned to your company."); setSearchParams({}, { replace: true }); }
    } else if (repair) {
      if (repairs.some((r) => r.id === repair)) setOpenRepairId(repair);
      else { toast.error("That repair request is not assigned to your company."); setSearchParams({}, { replace: true }); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading]);

  const pendingAck = leads.filter((l) => !l.acknowledged).length;

  if (authLoading || loading) {
    return <div className="min-h-screen grid place-items-center bg-[#EAF7FC]"><Loader2 className="animate-spin text-[#133955]" size={32} /></div>;
  }

  return (
    <div className="min-h-screen bg-[#EAF7FC]" data-testid="contractor-portal">
      <header className="bg-white border-b border-[rgba(19,57,85,0.08)] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <NotificationBell />
            <MessagesInbox />
            <button data-testid="contractor-logout" onClick={() => { logout(); navigate("/portal/login"); }} className="inline-flex items-center gap-2 rounded-full border-2 border-[#133955] text-[#133955] px-5 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white transition-colors">
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        {error && <p className="text-sm text-red-600 mb-4" data-testid="portal-error">{error}</p>}

        <div className="flex items-center gap-3 mb-6">
          <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#133955] text-white"><HardHat size={22} /></span>
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#133955] tracking-tight">
              {profile?.dba_name || profile?.legal_name}
            </h1>
            <p className="text-[#173042]/70 mt-0.5">Welcome back, {user?.name}</p>
          </div>
        </div>

        {pendingAck > 0 && (
          <div className="mb-5 rounded-xl bg-[#FFE8B0] text-[#7a5b00] px-4 py-3 text-sm font-semibold flex items-center gap-2" data-testid="pending-ack-banner">
            <ClipboardList size={16} /> You have {pendingAck} new project{pendingAck > 1 ? "s" : ""} to acknowledge.
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-5">
          <section className="md:col-span-1 rounded-2xl bg-white border border-[rgba(19,57,85,0.08)] p-6 self-start" data-testid="company-profile">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold text-[#133955]">Company Profile</h2>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${APPROVAL_BADGE[profile?.approval_status] || APPROVAL_BADGE.pending_approval}`} data-testid="contractor-approval-badge">
                {APPROVAL_LABEL[profile?.approval_status] || profile?.approval_status}
              </span>
            </div>
            <dl className="space-y-3 text-sm">
              <div><dt className="text-[#173042]/50 font-semibold uppercase text-xs tracking-wide">Contractor ID</dt><dd className="font-mono text-[#54BCE6] font-bold">{profile?.contractor_ref}</dd></div>
              <div><dt className="text-[#173042]/50 font-semibold uppercase text-xs tracking-wide">Legal Name</dt><dd className="text-[#173042]">{profile?.legal_name}</dd></div>
              {profile?.contact_name && <div><dt className="text-[#173042]/50 font-semibold uppercase text-xs tracking-wide">Primary Contact</dt><dd className="text-[#173042]">{profile.contact_name}</dd></div>}
              <div><dt className="text-[#173042]/50 font-semibold uppercase text-xs tracking-wide">Email</dt><dd className="text-[#173042] break-all">{profile?.email}</dd></div>
              {profile?.license_number && <div><dt className="text-[#173042]/50 font-semibold uppercase text-xs tracking-wide flex items-center gap-1"><ShieldCheck size={13} /> License</dt><dd className="text-[#173042]">{profile.license_number}</dd></div>}
              {profile?.referral_percentage != null && <div><dt className="text-[#173042]/50 font-semibold uppercase text-xs tracking-wide">Agreed Referral %</dt><dd className="text-[#133955] font-bold">{profile.referral_percentage}%</dd></div>}
            </dl>
            {profile?.services?.length > 0 && (
              <div className="mt-5">
                <p className="text-[#173042]/50 font-semibold uppercase text-xs tracking-wide flex items-center gap-1 mb-2"><Wrench size={13} /> Services</p>
                <div className="flex flex-wrap gap-2">{profile.services.map((s) => <Chip key={s}>{s}</Chip>)}</div>
              </div>
            )}
            {profile?.service_areas?.length > 0 && (
              <div className="mt-5">
                <p className="text-[#173042]/50 font-semibold uppercase text-xs tracking-wide flex items-center gap-1 mb-2"><MapPin size={13} /> Service Areas</p>
                <div className="flex flex-wrap gap-2">{profile.service_areas.map((s) => <Chip key={s}>{s}</Chip>)}</div>
              </div>
            )}
            {profile?.agreement && (
              <div className="mt-5 flex items-center gap-2 text-sm text-[#0f6b3d] bg-[#C7F0D8]/40 rounded-xl px-3 py-2" data-testid="agreement-status">
                <FileCheck2 size={16} /> Agreement on file
              </div>
            )}
          </section>

          <section className="md:col-span-2 rounded-2xl bg-white border border-[rgba(19,57,85,0.08)] p-6" data-testid="assigned-projects">
            <h2 className="font-display text-lg font-bold text-[#133955] mb-4">My Assigned Projects</h2>
            {leads.length === 0 ? (
              <div className="text-center py-16" data-testid="no-projects">
                <span className="grid place-items-center w-14 h-14 rounded-2xl bg-[#EAF7FC] text-[#54BCE6] mx-auto mb-4"><Inbox size={26} /></span>
                <p className="text-[#173042] font-semibold">No projects assigned yet</p>
                <p className="text-[#173042]/60 text-sm mt-1 max-w-sm mx-auto">When Home Pro Connect assigns a homeowner project to your company, it will appear here.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="project-list">
                {leads.map((l) => (
                  <button key={l.id} onClick={() => setOpenId(l.id)} data-testid={`assigned-lead-${l.id}`}
                    className="w-full text-left rounded-xl border border-[rgba(19,57,85,0.1)] p-4 hover:border-[#54BCE6] transition-colors">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        {l.lead_number ? <span className="font-mono text-sm font-bold text-[#54BCE6]">#{String(l.lead_number).padStart(4, "0")}</span> : null}
                        <span className="font-bold text-[#133955]">{l.project_type}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {l.my_role_label && <span className="rounded-full px-2.5 py-0.5 text-xs font-bold bg-[#EAF7FC] text-[#133955] border border-[#54BCE6]/40" data-testid={`portal-role-${l.id}`}>Role: {l.my_role_label}</span>}
                        {!l.acknowledged && <span className="rounded-full px-2.5 py-0.5 text-xs font-bold bg-[#FFE8B0] text-[#7a5b00]">Acknowledge</span>}
                        {l.project_status && <span className="rounded-full px-2.5 py-0.5 text-xs font-bold bg-[#133955] text-white" data-testid={`portal-project-status-${l.id}`}>{l.project_status}</span>}
                      </div>
                    </div>
                    <p className="text-sm text-[#173042]/70 mt-1">{l.city}{l.zip_code ? `, ${l.zip_code}` : ""}</p>
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>

        {repairs.length > 0 && (
          <section className="mt-5 rounded-2xl bg-white border border-[rgba(19,57,85,0.08)] p-6" data-testid="repair-requests-section">
            <h2 className="font-display text-lg font-bold text-[#133955] mb-4">Real Estate Repair Requests</h2>
            <div className="space-y-3" data-testid="repair-list">
              {repairs.map((r) => (
                <button key={r.id} onClick={() => setOpenRepairId(r.id)} data-testid={`repair-row-${r.id}`} className="w-full text-left rounded-xl border border-[rgba(19,57,85,0.1)] p-4 hover:border-[#54BCE6] transition-colors">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2"><span className="font-mono text-sm font-bold text-[#54BCE6]">{r.request_ref}</span><span className="font-bold text-[#133955]">{r.property_address}</span></div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.my_role_label && <span className="rounded-full px-2.5 py-0.5 text-xs font-bold bg-[#EAF7FC] text-[#133955] border border-[#54BCE6]/40" data-testid={`portal-repair-role-${r.id}`}>Role: {r.my_role_label}</span>}
                      <span className="rounded-full px-2.5 py-0.5 text-xs font-bold bg-[#133955] text-white">{r.status}</span>
                    </div>
                  </div>
                  <p className="text-sm text-[#173042]/70 mt-1">Agent: {r.agent_name}{r.agent_brokerage ? ` (${r.agent_brokerage})` : ""}</p>
                </button>
              ))}
            </div>
          </section>
        )}
      </main>

      <ProjectDetail leadId={openId} onClose={() => { setOpenId(null); if (searchParams.get("lead")) setSearchParams({}, { replace: true }); }} authHeader={authHeader} onChange={loadLeads} />
      <ContractorRepairDetail rid={openRepairId} onClose={() => { setOpenRepairId(null); if (searchParams.get("repair")) setSearchParams({}, { replace: true }); }} authHeader={authHeader} onChange={loadRepairs} />
    </div>
  );
}

const ACTION_LABEL = {
  lead_assigned: "Assigned", lead_co_assigned: "Co-assigned", lead_reassigned: "Reassigned", lead_unassigned: "Unassigned",
  assignment_role_updated: "Role updated",
  acknowledged: "Acknowledged", status_changed: "Status changed", contact_attempt: "Contact attempt", visit: "Site visit",
};

function ProjectDetail({ leadId, onClose, authHeader, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [contact, setContact] = useState({ method: "Phone", outcome: "", notes: "" });
  const [visit, setVisit] = useState({ scheduled_for: "", notes: "" });
  const [quoteForm, setQuoteForm] = useState({ amount: "", quote_date: "", scope: "", file: null });
  const [contractForm, setContractForm] = useState({ contract_value: "", contract_date: "", start_date: "", payment_schedule: "", file: null });

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/contractor/leads/${leadId}`, authHeader);
      setData(data);
    } catch (err) {
      toast.error("Could not load project.");
      onClose();
    } finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { if (leadId) load(); }, [leadId, load]);

  if (!leadId) return null;
  const lead = data?.lead;
  const statuses = data?.project_statuses || [];
  const methods = data?.contact_methods || ["Phone", "Email", "Text", "In Person", "Other"];
  const fileToken = (authHeader.headers.Authorization || "").replace("Bearer ", "");
  const referralPct = data?.referral_percentage;

  const refresh = async () => { await load(); onChange && onChange(); };

  const acknowledge = async () => {
    setBusy(true);
    try { await axios.post(`${API}/contractor/leads/${leadId}/acknowledge`, {}, authHeader); toast.success("Acknowledged."); await refresh(); }
    catch { toast.error("Could not acknowledge."); } finally { setBusy(false); }
  };
  const setStatus = async (project_status) => {
    setBusy(true);
    try { await axios.post(`${API}/contractor/leads/${leadId}/status`, { project_status }, authHeader); toast.success(`Status set to ${project_status}.`); await refresh(); }
    catch { toast.error("Could not update status."); } finally { setBusy(false); }
  };
  const submitContact = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/contractor/leads/${leadId}/contact-attempt`, contact, authHeader);
      toast.success("Contact attempt logged."); setContact({ method: "Phone", outcome: "", notes: "" }); await refresh();
    } catch { toast.error("Could not log contact attempt."); } finally { setBusy(false); }
  };
  const submitVisit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/contractor/leads/${leadId}/visit`, visit, authHeader);
      toast.success("Visit logged."); setVisit({ scheduled_for: "", notes: "" }); await refresh();
    } catch { toast.error("Could not log visit."); } finally { setBusy(false); }
  };
  const submitQuote = async () => {
    if (!quoteForm.amount || !quoteForm.quote_date) { toast.error("Amount and date are required."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("amount", quoteForm.amount);
      fd.append("quote_date", quoteForm.quote_date);
      fd.append("scope", quoteForm.scope);
      if (quoteForm.file) fd.append("file", quoteForm.file);
      await axios.post(`${API}/contractor/leads/${leadId}/quotes`, fd, { headers: { ...authHeader.headers, "Content-Type": "multipart/form-data" } });
      toast.success("Quote submitted."); setQuoteForm({ amount: "", quote_date: "", scope: "", file: null }); await refresh();
    } catch (err) { const d = err?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not submit quote."); } finally { setBusy(false); }
  };
  const submitContract = async () => {
    if (!contractForm.contract_value || !contractForm.contract_date) { toast.error("Contract value and date are required."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("contract_value", contractForm.contract_value);
      fd.append("contract_date", contractForm.contract_date);
      fd.append("start_date", contractForm.start_date);
      fd.append("payment_schedule", contractForm.payment_schedule);
      if (contractForm.file) fd.append("file", contractForm.file);
      await axios.post(`${API}/contractor/leads/${leadId}/contracts`, fd, { headers: { ...authHeader.headers, "Content-Type": "multipart/form-data" } });
      toast.success("Contract recorded."); setContractForm({ contract_value: "", contract_date: "", start_date: "", payment_schedule: "", file: null }); await refresh();
    } catch (err) { const d = err?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not record contract."); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!leadId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="project-detail">
        {loading || !lead ? (
          <>
            <DialogHeader>
              <DialogTitle className="sr-only">Project details</DialogTitle>
              <DialogDescription className="sr-only">Loading project details</DialogDescription>
            </DialogHeader>
            <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-[#133955]" size={28} /></div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-[#133955] flex items-center gap-2">
                {lead.lead_number ? <span className="font-mono text-[#54BCE6]">#{String(lead.lead_number).padStart(4, "0")}</span> : null}
                {lead.project_type}
              </DialogTitle>
              <DialogDescription>{lead.city}{lead.zip_code ? `, ${lead.zip_code}` : ""} · {lead.budget || "budget n/a"} · {lead.timeline || "timeline n/a"}</DialogDescription>
            </DialogHeader>

            {/* Homeowner contact */}
            <div className="rounded-xl bg-[#EAF7FC] p-4 text-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Homeowner Contact</p>
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-[#173042]/50">Name:</span> <strong className="text-[#133955]">{lead.name}</strong></div>
                <div><span className="text-[#173042]/50">Preferred:</span> {lead.preferred_contact || "—"}</div>
                <div className="break-all"><span className="text-[#173042]/50">Email:</span> <a href={`mailto:${lead.email}`} className="text-[#133955] font-semibold hover:underline">{lead.email}</a></div>
                <div><span className="text-[#173042]/50">Phone:</span> <a href={`tel:${lead.phone}`} className="text-[#133955] font-semibold hover:underline">{lead.phone}</a></div>
              </div>
              {lead.description && <p className="mt-3 text-[#173042]/80 whitespace-pre-wrap">{lead.description}</p>}
              {(lead.documents || []).length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Photos & Documents</p>
                  <AttachmentList docs={lead.documents} token={fileToken} testid="project-lead-documents" />
                </div>
              )}
            </div>

            {/* Notes from Home Pro Connect (visible to assigned contractors) */}
            {lead.notes?.trim() && (
              <div className="mt-4 rounded-xl bg-[#FFF7E0] border border-[#FFE8B0] p-4 text-sm" data-testid="project-lead-notes">
                <p className="text-xs font-bold uppercase tracking-wide text-[#7a5b00] mb-1.5 flex items-center gap-1.5"><StickyNote size={13} /> Notes from Home Pro Connect</p>
                <p className="text-[#173042]/85 whitespace-pre-wrap">{lead.notes}</p>
              </div>
            )}

            {/* Acknowledge + status */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {!lead.acknowledged ? (
                <button data-testid="acknowledge-btn" onClick={acknowledge} disabled={busy}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0f6b3d] text-white px-5 py-2.5 text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-60">
                  <Check size={15} /> Acknowledge Assignment
                </button>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0f6b3d]" data-testid="ack-done"><Check size={15} /> Acknowledged {lead.acknowledged_at ? `· ${fmtDate(lead.acknowledged_at)}` : ""}</span>
              )}
            </div>

            <div className="mt-4">
              <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Project Status</p>
              <div className="flex flex-wrap gap-2" data-testid="project-status-picker">
                {statuses.map((s) => (
                  <button key={s} data-testid={`set-project-status-${s.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => setStatus(s)} disabled={busy || lead.project_status === s}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${lead.project_status === s ? "bg-[#133955] text-white" : "bg-[#EAF7FC] text-[#133955] hover:bg-[#54BCE6]/40"} disabled:cursor-default`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Log contact + visit */}
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl border border-[rgba(19,57,85,0.1)] p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2 flex items-center gap-1.5"><Phone size={13} /> Log Contact Attempt</p>
                <select data-testid="contact-method" value={contact.method} onChange={(e) => setContact({ ...contact, method: e.target.value })} className="hpc-input bg-white mb-2">
                  {methods.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <input data-testid="contact-outcome" value={contact.outcome} onChange={(e) => setContact({ ...contact, outcome: e.target.value })} placeholder="Outcome (e.g. left voicemail)" className="hpc-input bg-white mb-2" />
                <button data-testid="log-contact-btn" onClick={submitContact} disabled={busy} className="w-full rounded-full bg-[#133955] text-white px-4 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-60">Log Contact</button>
              </div>
              <div className="rounded-xl border border-[rgba(19,57,85,0.1)] p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2 flex items-center gap-1.5"><CalendarClock size={13} /> Log Site Visit</p>
                <input data-testid="visit-date" type="datetime-local" value={visit.scheduled_for} onChange={(e) => setVisit({ ...visit, scheduled_for: e.target.value })} className="hpc-input bg-white mb-2" />
                <input data-testid="visit-notes" value={visit.notes} onChange={(e) => setVisit({ ...visit, notes: e.target.value })} placeholder="Notes (optional)" className="hpc-input bg-white mb-2" />
                <button data-testid="log-visit-btn" onClick={submitVisit} disabled={busy} className="w-full rounded-full bg-[#133955] text-white px-4 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-60">Log Visit</button>
              </div>
            </div>

            {/* Quotes & Contracts */}
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl border border-[rgba(19,57,85,0.1)] p-4" data-testid="quotes-section">
                <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2 flex items-center gap-1.5"><FileText size={13} /> Quotes</p>
                <div className="space-y-2 mb-3">
                  {(data.quotes || []).length === 0 && <p className="text-xs text-[#173042]/50">No quotes yet.</p>}
                  {(data.quotes || []).map((q) => (
                    <div key={q.id} className="text-sm flex items-center justify-between gap-2" data-testid={`quote-${q.id}`}>
                      <span className="text-[#133955] font-semibold">{money(q.amount)}</span>
                      <span className="text-xs text-[#173042]/50">{q.quote_date}</span>
                      {q.document && <a href={`${API}/files/${q.document.file_id}?auth=${fileToken}`} target="_blank" rel="noreferrer" className="text-[#54BCE6] hover:underline text-xs">doc</a>}
                    </div>
                  ))}
                </div>
                <input data-testid="quote-amount" type="number" min="0" value={quoteForm.amount} onChange={(e) => setQuoteForm({ ...quoteForm, amount: e.target.value })} placeholder="Amount ($)" className="hpc-input bg-white mb-2" />
                <input data-testid="quote-date" type="date" value={quoteForm.quote_date} onChange={(e) => setQuoteForm({ ...quoteForm, quote_date: e.target.value })} className="hpc-input bg-white mb-2" />
                <input data-testid="quote-scope" value={quoteForm.scope} onChange={(e) => setQuoteForm({ ...quoteForm, scope: e.target.value })} placeholder="Scope (optional)" className="hpc-input bg-white mb-2" />
                <input data-testid="quote-file" type="file" onChange={(e) => setQuoteForm({ ...quoteForm, file: e.target.files?.[0] || null })} className="text-xs mb-2 block w-full" />
                <button data-testid="submit-quote-btn" onClick={submitQuote} disabled={busy} className="w-full rounded-full bg-[#133955] text-white px-4 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-60">Submit Quote</button>
              </div>

              <div className="rounded-xl border border-[rgba(19,57,85,0.1)] p-4" data-testid="contracts-section">
                <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2 flex items-center gap-1.5"><FileCheck2 size={13} /> Contracts</p>
                <div className="space-y-2 mb-3">
                  {(data.contracts || []).length === 0 && <p className="text-xs text-[#173042]/50">No contracts yet.</p>}
                  {(data.contracts || []).map((c) => (
                    <div key={c.id} className="text-sm" data-testid={`contract-${c.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[#133955] font-semibold">{money(c.contract_value)}</span>
                        <span className="text-xs text-[#173042]/50">{c.contract_date}</span>
                        {c.document && <a href={`${API}/files/${c.document.file_id}?auth=${fileToken}`} target="_blank" rel="noreferrer" className="text-[#54BCE6] hover:underline text-xs">doc</a>}
                      </div>
                      <p className="text-xs text-[#0f6b3d] font-semibold">Referral {c.referral_percentage ?? Math.round((c.referral_rate || 0) * 100)}% = {money(c.referral_fee)}</p>
                    </div>
                  ))}
                </div>
                <input data-testid="contract-value" type="number" min="0" value={contractForm.contract_value} onChange={(e) => setContractForm({ ...contractForm, contract_value: e.target.value })} placeholder="Contract value ($)" className="hpc-input bg-white mb-1" />
                {contractForm.contract_value > 0 && (
                  referralPct != null ? (
                    <p className="text-xs text-[#0f6b3d] font-semibold mb-2" data-testid="referral-preview">
                      Referral fee: {referralPct}% = {money(Number(contractForm.contract_value) * (referralPct / 100))}
                    </p>
                  ) : (
                    <p className="text-xs text-[#7a5b00] font-semibold mb-2" data-testid="referral-preview">
                      Referral % not set yet — contact Home Pro Connect.
                    </p>
                  )
                )}
                <input data-testid="contract-date" type="date" value={contractForm.contract_date} onChange={(e) => setContractForm({ ...contractForm, contract_date: e.target.value })} className="hpc-input bg-white mb-2" />
                <input data-testid="contract-schedule" value={contractForm.payment_schedule} onChange={(e) => setContractForm({ ...contractForm, payment_schedule: e.target.value })} placeholder="Payment schedule (optional)" className="hpc-input bg-white mb-2" />
                <input data-testid="contract-file" type="file" onChange={(e) => setContractForm({ ...contractForm, file: e.target.files?.[0] || null })} className="text-xs mb-2 block w-full" />
                <button data-testid="submit-contract-btn" onClick={submitContract} disabled={busy} className="w-full rounded-full bg-[#133955] text-white px-4 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-60">Record Contract</button>
              </div>
            </div>

            {/* Activity timeline */}
            <div className="mt-5">
              <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Activity</p>
              <div className="space-y-2" data-testid="project-activity">
                {(data.activities || []).map((a) => (
                  <div key={a.id} className="flex items-start gap-3 text-sm">
                    <span className="mt-1 h-2 w-2 rounded-full bg-[#54BCE6] shrink-0" />
                    <div>
                      <p className="text-[#173042]"><strong className="text-[#133955]">{ACTION_LABEL[a.action] || a.action}</strong> — {a.description}</p>
                      <p className="text-xs text-[#173042]/50">{fmtDate(a.created_at)} · {a.actor_role}</p>
                    </div>
                  </div>
                ))}
                {(!data.activities || data.activities.length === 0) && <p className="text-sm text-[#173042]/50">No activity yet.</p>}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const R_ACTION = {
  repair_assigned: "Assigned", repair_reassigned: "Reassigned", quote_submitted: "Quote sent",
  quote_accepted: "Quote accepted", quote_declined: "Quote declined",
  walkthrough_requested: "Walk-through requested", walkthrough_proposed: "Time proposed", walkthrough_confirmed: "Walk-through confirmed",
};

function ContractorRepairDetail({ rid, onClose, authHeader, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState({ amount: "", scope: "", file: null });
  const [wt, setWt] = useState({ proposed_datetime: "", notes: "" });
  const fileToken = (authHeader.headers.Authorization || "").replace("Bearer ", "");

  const load = useCallback(async () => {
    if (!rid) return;
    setLoading(true);
    try { const { data } = await axios.get(`${API}/contractor/repair-requests/${rid}`, authHeader); setData(data); }
    catch { toast.error("Could not load request."); onClose(); }
    finally { setLoading(false); }
  }, [rid]); // eslint-disable-line

  useEffect(() => { if (rid) load(); }, [rid, load]);
  if (!rid) return null;
  const rr = data?.request;
  const w = rr?.walkthrough || { status: "none", proposals: [] };
  const latestAgentProp = [...(w.proposals || [])].reverse().find((p) => p.by === "agent");
  const refresh = async () => { await load(); onChange && onChange(); };

  const submitQuote = async () => {
    if (!quote.amount) { toast.error("Enter a quote amount."); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("amount", quote.amount); fd.append("scope", quote.scope); if (quote.file) fd.append("file", quote.file);
      await axios.post(`${API}/contractor/repair-requests/${rid}/quote`, fd, { headers: { ...authHeader.headers, "Content-Type": "multipart/form-data" } });
      toast.success("Quote sent to agent."); setQuote({ amount: "", scope: "", file: null }); await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send quote."); } finally { setBusy(false); }
  };
  const proposeWt = async () => {
    if (!wt.proposed_datetime) { toast.error("Pick a date/time."); return; }
    setBusy(true);
    try { await axios.post(`${API}/contractor/repair-requests/${rid}/walkthrough`, wt, authHeader); toast.success("Walk-through time sent to agent."); setWt({ proposed_datetime: "", notes: "" }); await refresh(); }
    catch { toast.error("Could not propose time."); } finally { setBusy(false); }
  };
  const confirmWt = async () => {
    setBusy(true);
    try { await axios.post(`${API}/contractor/repair-requests/${rid}/walkthrough/confirm`, {}, authHeader); toast.success("Walk-through confirmed."); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm."); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!rid} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contractor-repair-detail">
        {loading || !rr ? (
          <><DialogHeader><DialogTitle className="sr-only">Repair request</DialogTitle><DialogDescription className="sr-only">Loading</DialogDescription></DialogHeader><div className="grid place-items-center py-16"><Loader2 className="animate-spin text-[#133955]" size={28} /></div></>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-[#133955] flex items-center gap-2"><span className="font-mono text-[#54BCE6]">{rr.request_ref}</span> {rr.property_address}</DialogTitle>
              <DialogDescription>Status: {rr.status} · Agent: {rr.agent_name}</DialogDescription>
            </DialogHeader>

            <div className="rounded-xl bg-[#EAF7FC] p-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="break-all"><span className="text-[#173042]/50">Agent email:</span> <a href={`mailto:${rr.agent_email}`} className="text-[#133955] font-semibold hover:underline">{rr.agent_email}</a></div>
                <div><span className="text-[#173042]/50">Agent phone:</span> <a href={`tel:${rr.agent_phone}`} className="text-[#133955] font-semibold hover:underline">{rr.agent_phone || "—"}</a></div>
                {rr.mls_number && <div><span className="text-[#173042]/50">MLS:</span> {rr.mls_number}</div>}
                {rr.target_close_date && <div><span className="text-[#173042]/50">Close:</span> {rr.target_close_date}</div>}
              </div>
              <p className="mt-2 whitespace-pre-wrap text-[#173042]/80">{rr.repairs}</p>
            </div>

            {(rr.documents || []).length > 0 && (
              <div className="mt-4"><p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Agent Documents & Photos</p>
                <AttachmentList docs={rr.documents} token={fileToken} testid="contractor-repair-attachments" />
              </div>
            )}

            {/* Submit quote */}
            <div className="mt-4 rounded-xl border border-[rgba(19,57,85,0.1)] p-4" data-testid="repair-quote-form">
              <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Submit a Quote</p>
              {(data.quotes || []).map((q) => <div key={q.id} className="text-sm text-[#173042]/80 mb-1">{money(q.amount)} — <span className="capitalize font-semibold">{q.status}</span></div>)}
              <input data-testid="repair-quote-amount" type="number" min="0" value={quote.amount} onChange={(e) => setQuote({ ...quote, amount: e.target.value })} placeholder="Amount ($)" className="hpc-input bg-white mb-2" />
              <input data-testid="repair-quote-scope" value={quote.scope} onChange={(e) => setQuote({ ...quote, scope: e.target.value })} placeholder="Scope (optional)" className="hpc-input bg-white mb-2" />
              <input data-testid="repair-quote-file" type="file" onChange={(e) => setQuote({ ...quote, file: e.target.files?.[0] || null })} className="text-xs mb-2 block w-full" />
              <button data-testid="repair-quote-submit" onClick={submitQuote} disabled={busy} className="w-full rounded-full bg-[#133955] text-white px-4 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] disabled:opacity-60">Send Quote to Agent</button>
            </div>

            {/* Walk-through */}
            <div className="mt-4 rounded-xl border border-[rgba(19,57,85,0.1)] p-4" data-testid="repair-wt-form">
              <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2 flex items-center gap-1.5"><CalendarClock size={13} /> Request / Schedule Walk-through</p>
              {w.status === "confirmed" && <p className="text-sm font-semibold text-[#0f6b3d] mb-2">Confirmed for {w.confirmed_datetime}</p>}
              {(w.proposals || []).map((p) => <div key={p.id} className="text-sm text-[#173042]/80"><strong className="text-[#133955] capitalize">{p.by}:</strong> {p.proposed_datetime}{p.notes ? ` — ${p.notes}` : ""}</div>)}
              {w.status !== "confirmed" && latestAgentProp && (
                <button data-testid="repair-wt-confirm" onClick={confirmWt} disabled={busy} className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#0f6b3d] text-white px-4 py-2 text-sm font-bold hover:opacity-90 disabled:opacity-60"><Check size={14} /> Confirm {latestAgentProp.proposed_datetime}</button>
              )}
              {w.status !== "confirmed" && (
                <div className="mt-3">
                  <input data-testid="repair-wt-datetime" type="datetime-local" value={wt.proposed_datetime} onChange={(e) => setWt({ ...wt, proposed_datetime: e.target.value })} className="hpc-input bg-white mb-2" />
                  <input data-testid="repair-wt-notes" value={wt.notes} onChange={(e) => setWt({ ...wt, notes: e.target.value })} placeholder="Notes (optional)" className="hpc-input bg-white mb-2" />
                  <button data-testid="repair-wt-submit" onClick={proposeWt} disabled={busy} className="w-full rounded-full border-2 border-[#133955] text-[#133955] px-4 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white disabled:opacity-60">Propose Walk-through Time</button>
                </div>
              )}
            </div>

            <div className="mt-4"><p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Activity</p>
              <div className="space-y-2" data-testid="repair-activity">{(data.activities || []).map((a) => <div key={a.id} className="flex items-start gap-3 text-sm"><span className="mt-1 h-2 w-2 rounded-full bg-[#54BCE6] shrink-0" /><div><p className="text-[#173042]"><strong className="text-[#133955]">{R_ACTION[a.action] || a.action}</strong> — {a.description}</p><p className="text-xs text-[#173042]/50">{fmtDate(a.created_at)} · {a.actor_role}</p></div></div>)}</div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
